import {
    EmojiEvents,
    Explore,
    Flag,
    Lightbulb,
    LocationOn,
    MapsHomeWork,
    PermMedia,
    PlayCircleFilledWhite,
    PsychologyAlt,
    QrCodeScanner,
    QuestionAnswer,
    TaskAlt,
    WorkspacePremium
} from "@mui/icons-material";
import {ReactElement} from "react";

export interface Experience {
    id: string;
    title: string;
    description: string;
    type: ExperienceType;
    start_datetime: string | null;
    end_datetime: string | null;
    is_active: boolean;
    location_reference: string;
    price: number;
    max_participants: number | null;
    group_required: boolean;
    auto_group_enabled: boolean;
    group_min_size: number | null;
    group_max_size: number | null;
    allow_replay: boolean;
    created_by: string;
    organization_id: string;
    img_url: string;
    emoji: string | null;
    tags: string[];
    private_experience: boolean;
    chat_enabled: boolean;
    map_visible: boolean;
    ticket_visible: boolean;
    classifica_individuale_visible: boolean;
    classifica_gruppo_visible: boolean;
    code: string | null;
    status: ExperienceStatus;
    created_at: string;
    token_to_participant: boolean;
    permissions: PermissionType[];
    invite_email_subject: string | null;
    invite_email_body: string | null;
    invite_email_include_code: boolean;
}

export enum ExperienceType {
    permanent = "permanent",
    event = "event",
}

export enum PermissionType {
    NOTIFICHE = "NOTIFICHE",
    CAMERA = "CAMERA",
    LOCATION = "LOCATION",
    LOCATION_ALWAYS = "LOCATION_ALWAYS",
}

export enum MediaTypes {
    none = "none",
    image = "image",
    video = "video",
    audio = "audio",
}

export enum ExperienceStatus {
    draft = "draft",
    deleted = "deleted",
    public = "public",
}

export interface Organization {
    id: string;
    name: string;
    stripe_public_key: string;
    stripe_secret_key: string;
}

export type OrganizationInput = Omit<Organization, "id">;

// ── Fixed, reusable, location-bound catalog entries ─────────────────────────
// Created once (optionally without a location, for testing) and referenced
// by id from flow steps / experience tasks, instead of duplicating a raw QR
// string or a lat/lng pair every time. Location is bound/rebound later, from
// the React admin or from the Flutter app's Dev Tools (same /admin/v1 routes).

export interface QrCodeCatalogEntry {
    id: string;
    code: string;
    label: string | null;
    lat: number | null;
    lng: number | null;
    organization_id: string | null;
    created_by: string | null;
    created_at: string;
    updated_at: string;
}

export type GeocacheType = "QR_CODE" | "NFC";

export interface GeocacheCatalogEntry {
    id: string;
    label: string;
    description: string | null;
    lat: number | null;
    lng: number | null;
    type: GeocacheType;
    code: string;
    organization_id: string | null;
    created_by: string | null;
    created_at: string;
    updated_at: string;
}

export type FlowStep =
    | StartGame
    | EndGame
    | QrCodeScan
    | Quiz
    | Token
    | GoTo
    | Compass
    | Ranking
    | Question
    | Step
    | Semaforo

export interface Flow {
    id: string;
    title: string;
    steps: FlowStep[];
}

export enum FlowStepTypes {
    START_GAME = "START_GAME",
    END_GAME = "END_GAME",
    QR_CODE_SCAN = "QR_CODE_SCAN",
    QUIZ = "QUIZ",
    TOKEN = "TOKEN",
    GO_TO = "GO_TO",
    COMPASS = "COMPASS",
    RANKING = "RANKING",
    QUESTION = "QUESTION",
    STEP = "STEP",
    SEMAFORO = "SEMAFORO",
}

export interface BaseFlowStep {
    id: string;
    type: FlowStepTypes;
    displayTitle: string;
    displayDesc: string;
    hints: string[];
    hintsDelay: number; // in secondi
    completeUntil: number | null;
}
export interface StartGame extends BaseFlowStep {
    type: FlowStepTypes.START_GAME;
}

export interface QrCodeScan extends BaseFlowStep {
    type: FlowStepTypes.QR_CODE_SCAN;
    qrCodeId?: string | null; // reference into the qr_codes catalog (preferred)
    qrContent?: string; // legacy: raw string typed by hand, kept for old steps
}

export interface Quiz extends BaseFlowStep {
    type: FlowStepTypes.QUIZ;
    question: string;
    acceptedAnswers: string[]; // supporta più risposte corrette
    caseInsensitive?: boolean; // flag opzionale
    mediaType: MediaTypes;
    url: string;
    autoplay?: boolean;
}

export interface Token extends BaseFlowStep {
    type: FlowStepTypes.TOKEN;
    tokenLabel: string;
}

export interface GoTo extends BaseFlowStep {
    type: FlowStepTypes.GO_TO;
    targetLat: number;
    targetLng: number;
    radiusMeters?: number; // raggio di tolleranza
    locationLabel?: string;
}

export interface Compass extends BaseFlowStep {
    type: FlowStepTypes.COMPASS;
    targetLat: number;
    targetLng: number;
    radiusMeters?: number; // raggio di tolleranza
    locationLabel?: string;
    distanceVisible: boolean;
}

export interface Ranking extends BaseFlowStep {
    type: FlowStepTypes.RANKING;
    showIndividual: boolean;
    showGroups: boolean;
}

export interface Question extends BaseFlowStep {
    type: FlowStepTypes.QUESTION;
    question: string;
    answers: string[]; // first one is correct
    mediaType: MediaTypes;
    url: string;
    autoplay?: boolean;
}

export interface Step extends BaseFlowStep {
    type: FlowStepTypes.STEP;
}

export interface EndGame extends BaseFlowStep {
    type: FlowStepTypes.END_GAME;
    showFinalScore: boolean;
}

export interface Semaforo extends BaseFlowStep {
    type: FlowStepTypes.SEMAFORO;
}

export interface PlayerModel {
    user_id: string,
    experience_id: string | null,
    flow_id: string | null,
    is_leader: boolean,
    firstname: string | null,
    lastname: string | null,
    email: string,
    notifications_enabled: boolean | number,
}


export interface UserModel {
    id: string,
    firstname: string,
    lastname: string,
    email: string,
    phone: string,
    birthdate: string,
    gender: string,
    marketing_consent: boolean,
    created_at: string,
    apn_token: string,
    experiences_managed: string[],
}

export interface FlowsTitle {
    id: string,
    title: string,
}

export interface UserLocation {
    id: string;
    name: string;
    latitude: number;
    longitude: number;
    date: string;
}

export interface StickerModel {
    id: string;
    title: string;
    description: string;
    img_url: string;
    assigned_at?: string;
}

// ── Task System ───────────────────────────────────────────────────────────────

export enum TaskType {
    QUESTION = 'QUESTION',
    QR_CODE_SCAN = 'QR_CODE_SCAN',
    STEP = 'STEP',
    GEOCACHE = 'GEOCACHE',
}

export enum TriggerType {
    STEP_ACTIVATED = 'STEP_ACTIVATED',
    STEP_COMPLETE = 'STEP_COMPLETE',
    ANY_STEP_COMPLETE = 'ANY_STEP_COMPLETE',
    GEO_ZONE = 'GEO_ZONE',
    GEO_PROXIMITY = 'GEO_PROXIMITY',
    AFTER_STEP_DELAY = 'AFTER_STEP_DELAY',
    TIME_WINDOW = 'TIME_WINDOW',
    MANUAL = 'MANUAL',
}

export enum TaskTarget {
    individual = 'individual',
    group = 'group',
    both = 'both',
}

export enum NotificationEvent {
    ON_ACTIVATE = 'ON_ACTIVATE',
    ON_PROXIMITY = 'ON_PROXIMITY',
    ON_COMPLETE = 'ON_COMPLETE',
}

export enum NotificationTarget {
    all_participants = 'all_participants',
    group = 'group',
    individual = 'individual',
}

export interface TaskTrigger {
    id: string;
    task_id: string;
    trigger_type: TriggerType;
    config: Record<string, any>;
}

export interface TaskScoring {
    task_id: string;
    individual_points: number;
    group_points: number;
    target: TaskTarget;
    time_decay_enabled: boolean;
    time_decay_config: {
        base_points: number;
        min_points: number;
        decay_per_minute: number;
    } | null;
}

export interface TaskNotification {
    id: string;
    task_id: string;
    event_type: NotificationEvent;
    title: string;
    body: string;
    target: NotificationTarget;
}

export interface ExperienceTask {
    id: string;
    experience_id: string;
    name: string;
    description: string;
    type: TaskType;
    is_active: boolean;
    task_order: number;
    content: Record<string, any> | null;
    created_at: string;
    updated_at: string;
    triggers: TaskTrigger[];
    scoring: TaskScoring | null;
    notifications: TaskNotification[];
    rewardRules: TaskRewardRuleModel[];
}

export interface FlowForTrigger {
    id: string;
    title: string;
    steps: {
        id: string;
        flow_id: string;
        type: string;
        display_title: string;
        step_order: number;
    }[];
}

// ── Preventivi (quote requests from the Flutter app) ───────────────────────────
// Note the mixed casing: top-level fields are the snake_case DB columns, but
// `route`/`breakdown` are parsed straight from JSON produced by the Flutter
// client, which serializes them camelCase — left as-is rather than
// translated, so admin edits round-trip byte-identical to what the app sent.

export enum PreventivoStatus {
    bozza = 'bozza',
    inviato = 'inviato',
    in_lavorazione = 'in_lavorazione',
    approvato = 'approvato',
    rifiutato = 'rifiutato',
    in_revisione = 'in_revisione',
    annullato = 'annullato',
}

export interface PreventivoStatusHistoryEntry {
    id: string;
    status: PreventivoStatus;
    note: string | null;
    changed_by: string | null;
    changed_at: string;
}

export type PreventivoTappaType = 'venue' | 'poi' | 'custom';

export interface PreventivoTappaConsumazioni {
    enabled: boolean;
    drinkVariant?: string;
    foodVariant?: string;
}

// Same shape as PreventivoTappaConsumazioni — the winner's prize, chosen
// from the arrival venue's own drink/food list. Only ever meaningful on
// `route.endLocation`.
export interface PreventivoTappaPrize {
    enabled: boolean;
    drinkVariant?: string;
    foodVariant?: string;
}

export interface PreventivoTappaGamification {
    qrCode?: boolean;
    quiz?: boolean;
    quizDescription?: string;
    selectedGames?: string[];
}

export interface PreventivoTappa {
    id: string;
    type: PreventivoTappaType;
    name: string;
    coordinates: { lat: number; lng: number };
    imageUrl?: string;
    isPopolare?: boolean;
    consumazioni?: PreventivoTappaConsumazioni;
    gamification?: PreventivoTappaGamification;
    prize?: PreventivoTappaPrize;
}

export interface PreventivoPathChallenge {
    qrCode?: boolean;
    quiz?: boolean;
    quizDescription?: string;
}

// Global preference for which gamification game *types* the customer wants
// across the whole route (mobile-app-only concept, mirrors GameTypePreferences
// in gocicero_flutter's quote_models.dart). Sent by the Flutter quote wizard
// and stored as-is in route_json; not surfaced by the edit form on this page,
// only read back out for the PDF export.
export interface PreventivoGamePreferences {
    qrCodeHunt?: boolean;
    quiz?: boolean;
    geoCache?: boolean;
    sfide?: boolean;
}

export interface PreventivoRoute {
    startLocation?: PreventivoTappa;
    steps: PreventivoTappa[];
    endLocation?: PreventivoTappa;
    pathChallenges: Record<string, PreventivoPathChallenge>;
    gamePreferences?: PreventivoGamePreferences;
}

export interface PreventivoBreakdownItem {
    label: string;
    amount: number;
}

export interface Preventivo {
    id: string;
    user_id: string;
    status: PreventivoStatus;
    participants: number;
    event_date: string | null;
    zone: string | null;
    duration: string | null;
    time_slot: string | null;
    occasion: string | null;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone: string | null;
    total_price: number | null;
    admin_note: string | null;
    // Free-text special requests written by the customer in the wizard —
    // distinct from `admin_note`, which is the team's own internal note.
    additional_notes: string | null;
    created_at: string;
    updated_at: string;
    submitted_at: string | null;
    // Present only on the detail endpoint (GET /admin/v1/preventivi/:id), not
    // in the summary list.
    route?: PreventivoRoute;
    breakdown?: PreventivoBreakdownItem[];
    // Present only on the detail endpoint.
    statusHistory?: PreventivoStatusHistoryEntry[];
    pendingPayload?: Record<string, any> | null;
    preEditStatus?: PreventivoStatus | null;
}

// ── Awards ────────────────────────────────────────────────────────────────────

export interface AwardModel {
    id: string,
    user_id: string,
    title: string,
    description: string,
    valid_until: string,
    used: boolean,
    used_at: string | null,
}

// ── Experience reward rules (Premi & Sticker automatici) ───────────────────────

export type RewardRuleType = "AWARD" | "STICKER";
export type RewardRuleScope = "INDIVIDUAL_RANK" | "GROUP_RANK" | "ALL_PARTICIPANTS";

export interface RewardRuleModel {
    id: string;
    experience_id: string;
    reward_type: RewardRuleType;
    scope: RewardRuleScope;
    rank: number | null;
    sticker_id: string | null;
    sticker_title?: string | null;
    sticker_img_url?: string | null;
    award_title: string | null;
    award_description: string | null;
    award_valid_until: string | null;
    award_valid_days: number | null;
    is_active: boolean;
    created_at: string;
}

// ── Per-task reward rules (geocache/QR "chi arriva prima" premi) ───────────────

export type TaskRewardScope = "INDIVIDUAL" | "GROUP";

export interface TaskRewardRuleModel {
    id: string;
    task_id: string;
    scope: TaskRewardScope;
    limit_count: number | null; // null = tutti
    reward_type: RewardRuleType;
    sticker_id: string | null;
    award_title: string | null;
    award_description: string | null;
    award_valid_days: number | null;
    is_active: boolean;
    created_at: string;
}

export interface LiveMonitorModel {
    participationId: string
    flowId: string
    flowTitle: string
    leader: string
    participations: number
    lastStep: number
    totalSteps: number
    lastSemaforo: number
    totalSemaforo: number
    startedAt: string
    currentStep: {
        type: string
        displayTitle: string | null | undefined
        data: JSON
    }
    baseTimer: string
    completeUntil: number
}















export interface UserDataModel {
    uid: string
    name: string
    surname: string
    email: string
    terms: boolean
    marketing: boolean
    stripeCustomerId: string
    billingDetails: BillingDetailsModel | null
}

export interface BillingDetailsModel {
    address: string
    city: string
    country: string
    name: string
    streetnumber: boolean
    surname: boolean
    vat: string
}


export interface Subscription {
    id: string;
    object: string;
    cancel_at: number | null;
    cancel_at_period_end: boolean;
    canceled_at: number | null;
    cancellation_details: {
        comment: string | null;
        feedback: string | null;
        reason: string | null;
    };
    created: number;
    currency: string;
    current_period_end: number;
    current_period_start: number;
    customer: string;
    days_until_due: number | null;
    default_payment_method: string | null;
    default_source: string | null;
    default_tax_rates: any[];
    description: string | null;
    discount: any | null;
    discounts: any | null;
    ended_at: number | null;
    invoice_settings: {
        issuer: {
            type: string;
        };
    };
    items: {
        object: string;
        data: SubscriptionItem[];
        has_more: boolean;
        total_count: number;
        url: string;
    };
    latest_invoice: string;
    livemode: boolean;
    metadata: Record<string, any>;
    next_pending_invoice_item_invoice: any | null;
    on_behalf_of: any | null;
    pause_collection: any | null;
    payment_settings: {
        payment_method_options: any | null;
        payment_method_types: any | null;
        save_default_payment_method: string;
    };
    pending_invoice_item_interval: any | null;
    pending_setup_intent: any | null;
    pending_update: any | null;
    schedule: any | null;
    start_date: number;
    status: string;
    test_clock: any | null;
    transfer_data: any | null;
    trial_end: number | null;
    trial_settings: {
        end_behavior: {
            missing_payment_method: string;
        };
    };
    trial_start: number | null;
}

export interface SubscriptionItem {
    id: string;
    object: string;
    billing_thresholds: any | null;
    created: number;
    metadata: Record<string, any>;
    plan: Plan;
    price: Price;
    quantity: number;
    subscription: string;
    tax_rates: any[];
}


export interface Plan {
    id: string;
    object: string;
    active: boolean;
    aggregate_usage: string | null;
    amount: number;
    amount_decimal: string;
    billing_scheme: string;
    created: number;
    currency: string;
    discounts: any | null;
    interval: string;
    interval_count: number;
    livemode: boolean;
    metadata: Record<string, any>;
    nickname: string | null;
    product: string;
    tiers_mode: string | null;
    transform_usage: any | null;
    trial_period_days: number | null;
    usage_type: string;
}

export interface Recurring {
    aggregate_usage: string | null;
    interval: string;
    interval_count: number;
    trial_period_days: number | null;
    usage_type: string;
}

export interface Price {
    id: string;
    object: string;
    active: boolean;
    billing_scheme: string;
    created: number;
    currency: string;
    custom_unit_amount: any | null;
    livemode: boolean;
    lookup_key: string | null;
    metadata: Record<string, any>;
    nickname: string | null;
    product: string;
    recurring: Recurring | null;
    tax_behavior: string;
    tiers_mode: string | null;
    transform_quantity: any | null;
    type: string;
    unit_amount: number;
    unit_amount_decimal: string;
}

// ── Translations (dynamic DB strings, IT/EN) ────────────────────────────────

export type TranslatableEntityType =
    | "experience_task"
    | "experience_task_notification"
    | "flow"
    | "flow_step"
    | "experience"
    | "sticker"
    | "geocache";

export interface TranslationEntry {
    entity_type: TranslatableEntityType;
    entity_id: string;
    field_name: string;
    context_label: string;
    experience_id: string | null;
    experience_title: string | null;
    base_value: string;
    translated_value: string | null;
}

// ── Preventivi di Pagamento (Stripe Connect split-payment quotes) ──────────────
// Sistema DIVERSO e SEPARATO da "Preventivo"/PreventivoStatus sopra (che sono le
// richieste di preventivo esperienza inviate dall'app Flutter) — non confondere.

export enum PaymentQuoteStatus {
    bozza = 'bozza',
    inviato = 'inviato',
    pagato = 'pagato',
    fallito = 'fallito',
    scaduto = 'scaduto',
    annullato = 'annullato',
    rimborsato = 'rimborsato',
}

export interface PaymentRecipient {
    id: string;
    name: string;
    stripe_account_id: string;
    email: string | null;
    active: boolean;
    created_at: string;
    updated_at: string;
}

export interface PaymentQuoteItem {
    id: string;
    payment_quote_id: string;
    description: string;
    amount: number;
    note: string | null;
    sort_order: number;
}

export interface PaymentQuoteRecipientSplit {
    id: string;
    payment_quote_id: string;
    recipient_id: string;
    recipient_name?: string; // joined in on the detail endpoint
    amount: number;
    note: string | null;
    sort_order: number;
    stripe_transfer_id: string | null;
    transfer_status: 'pending' | 'transferred' | 'failed' | null;
    transferred_at: string | null;
}

export interface PaymentQuoteBillingInfo {
    payment_quote_id: string;
    country: string;
    customer_type: 'persona_fisica' | 'professionista' | 'azienda' | 'pa';
    first_name: string | null;
    last_name: string | null;
    company_name: string | null;
    tax_code: string | null;
    vat_number: string | null;
    address: string | null;
    street_number: string | null;
    postal_code: string | null;
    city: string | null;
    province: string | null;
    pec: string | null;
    sdi_code: string | null;
    created_at: string;
    updated_at: string;
}

export interface PaymentQuoteStatusHistoryEntry {
    id: string;
    status: PaymentQuoteStatus;
    note: string | null;
    changed_by: string | null;
    changed_at: string;
}

export interface PaymentQuote {
    id: string;
    status: PaymentQuoteStatus;
    title: string | null;
    customer_first_name: string | null;
    customer_last_name: string | null;
    customer_email: string;
    customer_phone: string | null;
    currency: string;
    total_amount: number;
    admin_note: string | null;
    created_at: string;
    updated_at: string;
    sent_at: string | null;
    paid_at: string | null;
    // Presenti solo sull'endpoint di dettaglio (GET /payment-quotes/v1/admin/:id).
    items?: PaymentQuoteItem[];
    splits?: PaymentQuoteRecipientSplit[];
    statusHistory?: PaymentQuoteStatusHistoryEntry[];
    billingInfo?: PaymentQuoteBillingInfo | null;
}